import _ from 'lodash'
import React from 'react'
import Cookie from 'js-cookie'
import queryString from 'query-string'
import styled from 'styled-components'
import apis from 'browser/app/models/apis'
import { copyShareableToolsURL } from '../utils'
import { WarningSign } from '@blueprintjs/icons'
import { Entity } from 'shared-libs/models/entity'
import { Popover, Position } from '@blueprintjs/core'
import DesktopComponentsMap from 'browser/components/index'
import { Toast } from 'browser/mobile/components/toast/toast'
import { EntityRenderer } from 'shared-libs/components/entity/renderer'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { SandboxError, SandboxSession } from 'shared-libs/helpers/sandbox/sandbox-session'
import { Section } from 'browser/components/atomic-elements/atoms/section/section'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import { AppNavigatorContext } from 'browser/contexts/app-navigator/app-navigator-context'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { QRCodeView } from 'browser/components/atomic-elements/atoms/qrcode-view/qrcode-view'
import { InputField } from 'browser/components/atomic-elements/molecules/fields/input-field/input-field'

export type ConnectionConfig = {
  /** sandbox server url override */
  serverUrl?: string
  /** sandbox server port override */
  port?: string
}

interface ISafeRendererProps {
  selectedPlatform: string
  entity: Entity
  schema: Entity
  /** hook for Entity updates in a standard EntityRenderer */
  onChange: (entity: Entity) => void
  /** hook for entity json content updates from sandbox client */
  onChangeContent: (content: any) => void
  onCopyMobileDeeplinkClick: () => void
  onConfigUpdate: (config: ConnectionConfig) => void
}

export class SandboxRenderer extends React.Component<ISafeRendererProps> {
  private mobileRendererRef = React.createRef<MobileRenderer>()

  public render() {
    const {
      schema,
      selectedPlatform,
      entity,
      onChange,
      onChangeContent,
      onCopyMobileDeeplinkClick: onCopyMobileDeeplinkClick,
      onConfigUpdate,
    } = this.props

    if (selectedPlatform === 'mobile' || selectedPlatform === 'mobileWeb') {
      return (
        <MobileRenderer
          ref={this.mobileRendererRef}
          target={selectedPlatform}
          schema={schema}
          entity={entity}
          onChangeContent={onChangeContent}
          onRequestCopyMobileDeeplink={onCopyMobileDeeplinkClick}
          onConfigUpdate={onConfigUpdate}
        />
      )
    }

    return (
      <AppNavigatorContext.Consumer>
        {({ settings }) => (
          <EntityRenderer
            value={entity}
            schema={schema}
            state={{ settings }}
            uiSchemaPath="uiSchema"
            componentsMap={DesktopComponentsMap}
            onChangeComplete={onChange}
            translationTable={globalTranslationTable}
          />
        )}
      </AppNavigatorContext.Consumer>
    )
  }

  /* forwards the latest content to the mobile renderer / sandbox client, if active */
  public sendLatestValue = (entityJson: any, schemaJson: any) => {
    this.mobileRendererRef.current?.sendLatestValue(entityJson, schemaJson)
  }
}

interface IMobileRendererProps {
  target: 'mobile' | 'mobileWeb'
  schema: Entity
  entity: Entity
  onChangeContent: (content: any) => void
  onRequestCopyMobileDeeplink: () => void
  onConfigUpdate: (config: ConnectionConfig) => void
}

interface IMobileRendererState {
  /**
   * The sandbox server's own IP address. A convenience for debug builds running
   * on a physical device.
   */
  serverAddress?: string
  isConnected?: boolean
  error?: any
}

/**
 * Registers this desktop editor as the sandbox host and broadcasts the current
 * schema + entity JSON to connected clients via the sandbox-server.
 */
class MobileRenderer extends React.Component<IMobileRendererProps, IMobileRendererState> {
  private session: SandboxSession

  constructor(props: IMobileRendererProps) {
    super(props)

    this.state = {}
    this.session = new SandboxSession({
      role: 'host',
      apis,
      onConnected: this.handleSandboxConnected,
      onDisconnected: this.handleSandboxDisconnected,
      onError: this.handleSandboxError,
      onRegisteredHost: this.handleRegisteredHost,
      onEntityContent: (entityJson) => this.props.onChangeContent(entityJson),
    })
  }

  public componentDidMount(): void {
    this.connect()
  }

  public componentWillUnmount(): void {
    this.session.close()
  }

  public render() {
    const { error } = this.state
    return (
      <div className="pa2">
        <div>{error && this.renderError(error)}</div>
        {this.renderReconnect()}
        {this.renderServerUrl()}
      </div>
    )
  }

  private renderReconnect() {
    const { target } = this.props
    const { serverUrl, port } = queryString.parse(location.search)
    const { isConnected } = this.state

    if (isConnected) {
      return null
    }

    const targetLabel = target === 'mobile' ? 'a mobile device' : 'mobile web'

    return (
      <div className="tc">
        <div className="c-sandboxNote mt2 mb2 pre-wrap tl">
          To render this UI schema on {targetLabel}, the sandbox server must be running. If you need
          to start the sandbox server, see the <strong>scripts/sandbox-server</strong> project. Once
          that is running, you may configure the URL below as needed.
        </div>
        <Section title="Connection Config">
          <InputField
            label="Sandbox Server URL"
            isDisabled={true}
            value={this.getSandboxServerUrl()}
          />
          <InputField
            label="Port Override"
            placeholder="Enter a port to override the default"
            isDisabled={!!serverUrl}
            onChange={this.handleServerPortChange}
            value={!serverUrl ? port : 'ignored'}
          />
          <HelpBlock>
            This overrides the default port of 3000, in case you are running the sandbox server on a
            non-default port.
          </HelpBlock>
          <InputField
            label="URL Override"
            placeholder="Enter a URL to override the default"
            onChange={this.handleServerUrlChange}
            value={serverUrl}
          />
          <HelpBlock className="pre-wrap">
            This should be a WebSocket url pointing to the running sandbox server.
            {'\n'}
            {'\n'}
          </HelpBlock>
        </Section>
        <Button intent="primary" buttonText="Connect" onClick={this.handleConnectClicked} />
        {target === 'mobile' && this.renderCleartextWarning()}
      </div>
    )
  }

  private renderCleartextWarning() {
    return (
      <>
        <hr className="mt4 mb4" />
        <div className="c-sandboxNote pre-wrap tl">
          <WarningSign /> <strong>WARNING</strong>
          {'\n'}
          {'\n'}
          The default sandbox server URL {this.getDefaultSandboxServerUrl()} will only work with the{' '}
          <strong>DEBUG</strong> variant of the mobile app, which permits HTTP/cleartext traffic.
          (In this case, the sandbox server sends its IP address back to web, and web offers that as
          a QR code for mobile to connect to over LAN.)
          {'\n'}
          {'\n'}
          If you{"'"}re using the <strong>RELEASE</strong> variant of the mobile app, that will not
          work, as cleartext HTTP is blocked. You will need to use a reverse proxy so that your
          mobile device can connect to the sandbox server over HTTPS. You may use our{' '}
          <a href="https://www.notion.so/Secure-tunnels-with-CloudFlare-e24cc8aeacc447d9abaca605a7cdf2c5">
            CloudFlare tunnel script
          </a>
          , <a href="https://ngrok.com/download">ngrok</a>, etc. Make sure to use{' '}
          <strong>wss://</strong> for the scheme instead of ws://, for example
          wss://sandbox123.local-323200.com/ or wss://0135-174-160-108-120.ngrok-free.app. Once you
          have obtained that URL, enter it below for the URL Override before clicking Connect.
        </div>
      </>
    )
  }

  private handleServerUrlChange = (serverUrl: string) => {
    const { onConfigUpdate } = this.props
    onConfigUpdate({ serverUrl })
  }

  private handleServerPortChange = (port: string) => {
    const { onConfigUpdate } = this.props
    onConfigUpdate({ port })
  }

  private renderError(error) {
    return (
      <ErrorMessageContainer>
        <ErrorTitle>{`${error.type} error:`}</ErrorTitle>
        <ErrorText>{error.message}</ErrorText>
      </ErrorMessageContainer>
    )
  }

  private renderServerUrl() {
    const { isConnected } = this.state
    if (!isConnected) {
      return null
    }

    return (
      <div className="tc pa2">
        <div className="mb2">Connected to the sandbox server at {this.getSandboxServerUrl()}</div>
        <Button buttonText="Disconnect" onClick={this.handleDisconnectClicked} />
        {this.props.target === 'mobile'
          ? this.renderMobileConnected()
          : this.renderMobileWebConnected()}
      </div>
    )
  }

  private renderMobileConnected() {
    const { onRequestCopyMobileDeeplink } = this.props

    const localhostWarning = this.getSandboxServerUrl().includes('localhost')
      ? "Note: the release build can't connect to a local address over ws://. Disconnect and use a wss:// reverse proxy instead."
      : ''

    return (
      <>
        <HelpBlock className="pre-wrap">{localhostWarning}</HelpBlock>

        <div className="mt4">
          <strong className="mb4">
            Mobile app &gt; Menu &gt; Settings &gt; Tap the {'"'}Version{'"'} number 10 times
          </strong>
        </div>
        <div className="mt2">and scan this QR code to connect:</div>

        <QRCodeView className="mt4" qrValue={this.getMobileSandboxServerUrl()} qrSize={256} />
        <HelpBlock className="pre-wrap">
          Sandbox server{"'"}s local address: {this.getMobileSandboxServerUrl()}
        </HelpBlock>

        <hr className="mt4 mb4" />

        <Button buttonText="Copy deeplink" onClick={onRequestCopyMobileDeeplink} />
        <HelpBlock className="pre-wrap">
          The mobile sandbox deeplink enables opening the sandbox on mobile and rendering its
          jsonProps string independently of the desktop web sandbox page and sandbox-server, useful
          for automated UI testing for example.
        </HelpBlock>
      </>
    )
  }

  private renderMobileWebConnected() {
    const url = this.getMobileWebSandboxUrl()
    const serverAddress = this.getMobileSandboxServerUrl()
    return (
      <>
        <div className="mt3 mr4 ph3 tl">
          While running mobile-web, you have two options for connecting a sandbox client:
        </div>
        <ul className="mt1 tl pa3">
          <li className="mb2">
            <strong>On this machine:</strong> open{' '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>{' '}
            in a new window. This can work fine if you don't actually need the mobile platform.
          </li>
          <li className="mb2">
            <strong>On a mobile device:</strong> see below. This is preferred if your testing
            requires the mobile platform in some way.
          </li>
        </ul>
        <div className="tl">
          In either case, if you're already logged in on the client device, then navigating to the{' '}
          <strong>/sandbox</strong> path in the mobile browser address bar should show you the
          sandbox client connection screen.
        </div>
        <div className="tl">
          There, you can either scan the QR code below to connect, or input its local address (
          <strong>{serverAddress}</strong>) and tap connect.
        </div>

        <QRCodeView className="mt3" qrValue={serverAddress} qrSize={200} />

        <hr className="mt4 mb3" />
        <div className="tl mb2 pre-wrap">
          If you're not logged in on the mobile-web client, you can click the button below to reveal
          a QR code representing an authenticated sandbox URL for your current user that you can
          scan with the device's system camera, or copy the link. Note that it replaces any existing
          mobile-web session on that device with your token!
        </div>
        {this.renderAuthenticatedQr()}
      </>
    )
  }

  private renderAuthenticatedQr() {
    const url = this.getAuthenticatedMobileWebSandboxUrl()
    const isLocalhost = new URL(url).hostname === 'localhost'
    const popoverContent = (
      <div className="pa4 tc" style={{ width: 420 }}>
        <QRCodeView qrValue={url} qrSize={320} />
        <Button
          className="mt3"
          buttonText="Copy link"
          onClick={() =>
            copyShareableToolsURL(url, 'Authenticated sandbox link copied to clipboard')
          }
        />
        {isLocalhost && (
          <div className="c-sandboxNote mt3 tl pre-wrap">
            <WarningSign />
            This link points at <strong>localhost</strong>. If the mobile device does not have a
            reverse proxy set up to the machine that hosts the mobile-web webpack-dev-server (e.g.
            `adb reverse tcp:4201 tcp:4201`), then once you open this link on the device you'll need
            to replace <strong>localhost</strong> with the IP address of the machine running that
            dev server.
          </div>
        )}
      </div>
    )
    return (
      <Popover content={popoverContent} position={Position.BOTTOM}>
        <span>
          <Button buttonText="Sign in on device via QR" />
        </span>
      </Popover>
    )
  }

  private connect = _.debounce(() => {
    this.session.connect(this.getSandboxServerUrl())
  }, 300)

  private getSandboxServerUrl() {
    const { serverUrl } = queryString.parse(location.search)

    if (serverUrl) {
      return serverUrl
    }
    return this.getDefaultSandboxServerUrl()
  }

  private getDefaultSandboxServerUrl() {
    return `ws://localhost:${this.getPort()}`
  }

  /* Provides a sandbox url suitable for the mobile device. */
  private getMobileSandboxServerUrl() {
    const { serverUrl } = queryString.parse(location.search)
    const { serverAddress } = this.state

    if (serverUrl) {
      return serverUrl
    } else {
      return `ws://${serverAddress}`
    }
  }

  private getMobileWebSandboxUrl() {
    const { serverUrl } = queryString.parse(location.search)
    const url = new URL('/sandbox', this.getMobileWebOrigin())
    if (serverUrl) {
      url.searchParams.set('serverUrl', serverUrl as string)
    }
    return url.toString()
  }

  private getAuthenticatedMobileWebSandboxUrl() {
    const url = new URL('/sandbox', this.getMobileWebOrigin())
    url.searchParams.set('serverUrl', this.getMobileSandboxServerUrl())
    const accessToken = Cookie.get('Authorization')?.replace('Bearer ', '')
    if (accessToken) {
      url.searchParams.set('access_token', accessToken)
    }
    return url.toString()
  }

  /* best-effort origin of the mobile-web app; local dev serves it on its own
   * port, and MOBILEWEB_URL is only a valid absolute origin in remote envs */
  private getMobileWebOrigin() {
    const candidate = apis.MOBILEWEB_URL
    if (/^https?:\/\//.test(candidate) && !candidate.includes('localhost')) {
      return candidate
    }
    return 'http://localhost:4201'
  }

  private getPort() {
    const { port } = queryString.parse(location.search)
    return port || 3000
  }

  private handleConnectClicked = () => {
    this.setState({ error: undefined }, this.connect)
  }

  private handleDisconnectClicked = () => {
    this.session.close()
    this.setState({ isConnected: false })
  }

  private handleSandboxConnected = () => {
    const { schema, entity } = this.props
    console.log('connected to sandbox server')
    Toast.show({ message: 'connected to sandbox server' })
    this.session.sendLatestValue(entity.content, schema.content)
    this.setState({ isConnected: true })
  }

  private handleRegisteredHost = (serverAddress: string) => {
    this.setState({ serverAddress })
  }

  private handleSandboxError = (error: SandboxError) => {
    console.error('sandbox server error:', error)
    // connection errors carry no useful message; registration errors do
    const message =
      error.type === 'connection'
        ? `Failed to connect to a sandbox server at ${this.getSandboxServerUrl()}.`
        : error.message
    this.setState({ error: { type: 'connection', message } })
  }

  private handleSandboxDisconnected = () => {
    const defaultError = { type: 'connection', message: 'Disconnected from the sandbox server' }
    // preserve existing error if handling a disconnect after an error
    const { error = defaultError } = this.state
    console.log('disconnected from the sandbox server')
    Toast.show({ message: 'disconnected from the sandbox server' })
    this.setState({ isConnected: false, error })
  }

  public sendLatestValue = (entityJson: any, schemaJson: any) => {
    this.session.sendLatestValue(entityJson, schemaJson)
  }
}

const ErrorMessageContainer = styled.div`
  background-color: #ffebee;
  border-radius: 5px;
  justify-content: center;
  margin-top: 5px;
`

const ErrorTitle = styled.div`
  font-weight: bold;
  color: #d45c43;
  padding-right: 5px;
  padding-left: 5px;
`

const ErrorText = styled.div`
  color: #d45c43;
  padding-right: 5px;
  padding-left: 10px;
`
